import { useEffect, useState } from "react";
import { TextField, Dialog, DialogActions, DialogContent, DialogTitle, Button, Alert, Select, MenuItem, FormControl, InputLabel, IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import axios from "axios";
import Swal from "sweetalert2";

export default function MainSettingForm({ open, handleClose, rowData }) {
    const [newData, setNewData] = useState({});
    const [errors, setErrors] = useState({});
    const [helperText, setHelperText] = useState({});
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    
    const handleClickShowPassword = () => setShowPassword((show) => !show);
    
    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const fetchUserEmailSettings = async (userId) => {
        try {
            setIsLoading(true);
            const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/user-email-settings/${userId}`);
            if (response.status === 200) {
                if (response.data.data.setting === null) {
                    setNewData({
                        smtp_host: "",
                        smtp_port: "",
                        smtp_username: "",
                        smtp_password: "",
                        smtp_encryption: "none",
                        smtp_from_email: "",
                        smtp_from_name: "",
                        is_active: true
                    });
                    setErrorMessage("");
                } else {
                    const settingData = response.data.data.setting;
                    setNewData({
                        smtp_host: settingData.smtp_host || "",
                        smtp_port: settingData.smtp_port || "",
                        smtp_username: settingData.smtp_username || "",
                        smtp_password: settingData.smtp_password || "",
                        smtp_encryption: settingData.smtp_encryption || "none",
                        smtp_from_email: settingData.smtp_from_email || "",
                        smtp_from_name: settingData.smtp_from_name || "",
                        is_active: settingData.is_active ?? true
                    });
                }
            }
        } catch (error) {
            setErrorMessage(error.response?.data?.message || "Failed to fetch email settings");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (open && rowData?.user_id) {
            fetchUserEmailSettings(rowData.user_id);
        } else if (open) {
            setNewData({
                smtp_host: "",
                smtp_port: "",
                smtp_username: "",
                smtp_password: "",
                smtp_encryption: "none",
                smtp_from_email: "",
                smtp_from_name: "",
                is_active: true
            });
        }
        setErrors({});
        setHelperText({});
        setErrorMessage("");
    }, [open, rowData]);

    const setError = async (field, message) => {
        await setErrors(prevErrors => ({ ...prevErrors, [field]: true }));
        await setHelperText(prevHelperText => ({ ...prevHelperText, [field]: message }));
    };

    const validate = async (field, value) => {
        switch (field) {
            case "smtp_host":
                if (!value) {
                    await setError("smtp_host", "SMTP host is required.");
                }
                break;
            case "smtp_port":
                if (!value || value < 1 || value > 65535) {
                    await setError("smtp_port", "Port must be between 1 and 65535.");
                }
                break;
            case "smtp_username":
                if (!value) {
                    await setError("smtp_username", "SMTP username is required.");
                }
                break;
            case "smtp_password":
                if (!value) {
                    await setError("smtp_password", "SMTP password is required.");
                }
                break;
            case "smtp_from_email":
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!value || !emailRegex.test(value)) {
                    await setError("smtp_from_email", "Please enter a valid email address.");
                }
                break;
            default:
                break;
        }

        if (!errors[field]) {
            await setErrors((prevErrors) => ({ ...prevErrors, [field]: false }));
            await setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: "" }));
        }
    };

    const handleChangeMailSetting = async () => {
        let isValid = true;
        const requiredFields = ["smtp_host", "smtp_port", "smtp_username", "smtp_password", "smtp_from_email"];

        for (const field of requiredFields) {
            if (!newData[field]) {
                isValid = false;
                await validate(field, newData[field]);
            }
        }

        if (newData.smtp_port) {
            const portNum = parseInt(newData.smtp_port);
            if (isNaN(portNum) || portNum < 1 || portNum > 65535) {
                isValid = false;
                await setError("smtp_port", "Port must be between 1 and 65535.");
            }
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newData.smtp_from_email)) {
            isValid = false;
            await setError("smtp_from_email", "Please enter a valid email address.");
        }

        if (isValid) {
            await setErrorMessage("");
            await changeMainSetting();
        } else {
            await setErrorMessage("Please fill all the required fields correctly.");
        }
    };

    const changeMainSetting = async () => {
        try {
            const formattedData = {
                user_id: parseInt(rowData.user_id),
                smtp_host: newData.smtp_host?.trim(),
                smtp_port: parseInt(newData.smtp_port),
                smtp_username: newData.smtp_username?.trim(),
                smtp_password: newData.smtp_password,
                smtp_encryption: newData.smtp_encryption || 'none',
                smtp_from_email: newData.smtp_from_email?.trim(),
                smtp_from_name: newData.smtp_from_name?.trim() || null,
                is_active: true
            };

            const response = await axios.put(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/user-email-settings/upsert`, formattedData);

            if (response.status === 200) {
                handleClose();
                Swal.fire({
                    title: "Success",
                    text: "Mail settings updated successfully",
                    icon: "success",
                    showConfirmButton: false,
                    timer: 2000,
                });
            }
        } catch (error) {
            if (error.response && error.response.data) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage("An error occurred while updating mail settings. Please try again later.");
            }
        }
    };

    return (
        <Dialog
            fullWidth
            open={open}
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">User Mail Setting</DialogTitle>
            {errorMessage && (
                <Alert variant="outlined" severity="error" style={{ marginLeft: "24px", marginRight: "24px" }}>
                    {errorMessage}
                </Alert>
            )}
            <DialogContent style={{ paddingBottom: "0px" }}>
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="smtp_host"
                    label="SMTP Host"
                    placeholder="e.g., smtp.gmail.com"
                    type="text"
                    fullWidth
                    required
                    disabled={isLoading}
                    value={newData.smtp_host || ""}
                    error={errors.smtp_host}
                    helperText={helperText.smtp_host}
                    onChange={(e) => {
                        setNewData({ ...newData, smtp_host: e.target.value });
                        validate("smtp_host", e.target.value);
                    }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="smtp_port"
                    label="SMTP Port"
                    placeholder="e.g., 587 for TLS, 465 for SSL"
                    type="number"
                    fullWidth
                    required
                    disabled={isLoading}
                    value={newData.smtp_port || ""}
                    error={errors.smtp_port}
                    helperText={helperText.smtp_port}
                    onChange={(e) => {
                        setNewData({ ...newData, smtp_port: parseInt(e.target.value) });
                        validate("smtp_port", e.target.value);
                    }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="smtp_username"
                    label="SMTP Username"
                    placeholder="e.g., your.email@gmail.com"
                    type="text"
                    fullWidth
                    required
                    disabled={isLoading}
                    value={newData.smtp_username || ""}
                    error={errors.smtp_username}
                    helperText={helperText.smtp_username}
                    onChange={(e) => {
                        setNewData({ ...newData, smtp_username: e.target.value });
                        validate("smtp_username", e.target.value);
                    }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="smtp_password"
                    label="SMTP Password"
                    placeholder="Enter your SMTP password or app-specific password"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    required
                    disabled={isLoading}
                    value={newData.smtp_password || ""}
                    error={errors.smtp_password}
                    helperText={helperText.smtp_password}
                    onChange={(e) => {
                        setNewData({ ...newData, smtp_password: e.target.value });
                        validate("smtp_password", e.target.value);
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end"
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <FormControl fullWidth margin="dense">
                    <InputLabel id="smtp-encryption-label">SMTP Encryption</InputLabel>
                    <Select
                        labelId="smtp-encryption-label"
                        id="smtp_encryption"
                        value={newData.smtp_encryption || 'none'}
                        label="SMTP Encryption"
                        disabled={isLoading}
                        onChange={(e) => setNewData({ ...newData, smtp_encryption: e.target.value })}
                    >
                        <MenuItem value="none">None</MenuItem>
                        <MenuItem value="ssl">SSL</MenuItem>
                        <MenuItem value="tls">TLS</MenuItem>
                    </Select>
                </FormControl>
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="smtp_from_email"
                    label="From Email"
                    placeholder="e.g., noreply@yourcompany.com"
                    type="email"
                    fullWidth
                    required
                    disabled={isLoading}
                    value={newData.smtp_from_email || ""}
                    error={errors.smtp_from_email}
                    helperText={helperText.smtp_from_email}
                    onChange={(e) => {
                        setNewData({ ...newData, smtp_from_email: e.target.value });
                        validate("smtp_from_email", e.target.value);
                    }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="smtp_from_name"
                    label="From Name"
                    placeholder="e.g., Company Name or Department"
                    type="text"
                    fullWidth
                    disabled={isLoading}
                    value={newData.smtp_from_name || ""}
                    onChange={(e) => {
                        setNewData({ ...newData, smtp_from_name: e.target.value });
                    }}
                />
            </DialogContent>
            <DialogActions style={{ margin: "12px" }}>
                <Button 
                    onClick={handleChangeMailSetting} 
                    variant="contained" 
                    color="primary" 
                    disabled={isLoading}
                    style={{ marginRight: "6px", color: "white" }}
                >
                    {isLoading ? "Loading..." : "Save"}
                </Button>
                <Button 
                    onClick={handleClose} 
                    variant="contained" 
                    color="error" 
                    disabled={isLoading}
                    style={{ marginRight: "6px", color: "white" }}
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
